import { useEffect, useRef } from 'react';

interface AlertModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  buttonText?: string;
  onClose: () => void;
  variant?: 'info' | 'success' | 'warning' | 'error';
}

export function AlertModal({
  isOpen,
  title,
  message,
  buttonText = 'OK',
  onClose,
  variant = 'info'
}: AlertModalProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Focus button when modal opens
      buttonRef.current?.focus();

      // Handle escape key
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const variantStyles = {
    info: 'bg-blue-600 hover:bg-blue-700',
    success: 'bg-green-600 hover:bg-green-700',
    warning: 'bg-yellow-600 hover:bg-yellow-700',
    error: 'bg-red-600 hover:bg-red-700'
  };

  const iconStyles = {
    info: '💡',
    success: '✅',
    warning: '⚠️',
    error: '❌'
  };

  return (
    <div className='bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black px-4'>
      <div className='w-full max-w-md rounded-lg bg-white p-4 shadow-xl md:p-6 dark:bg-gray-800'>
        <div className='mb-4 flex items-center gap-3'>
          <span className='text-2xl'>{iconStyles[variant]}</span>
          <h2 className='text-xl font-bold text-gray-900 dark:text-white'>{title}</h2>
        </div>
        <p className='mb-6 text-gray-700 dark:text-gray-300'>{message}</p>
        <div className='flex justify-end'>
          <button
            ref={buttonRef}
            onClick={onClose}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white ${variantStyles[variant]}`}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  );
}
